import React, { useEffect, useRef, useState } from "react";
import questions from "../../../utils/QuestionSection";
import { FaArrowLeft, FaArrowRight, FaPaperPlane } from "react-icons/fa";
import {
  config,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
  successNotify,
} from "../../../utils/helper";
import {
  authorizedGet,
  authorizedPost,
} from "../../../Config/networkWithToken";
import configuration from "../../../Config/configuration";

const QuestionnaireForm = (
  currentStep,
  setCurrentStep,
  steps,
  userDetails,
  userRole,
  setUserDetails,
  setIsNext,
) => {
  const sectionKeys = Object.keys(questions);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const containerRef = useRef(null);
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const currentSectionKey = sectionKeys[currentSectionIndex];
  const currentSection = questions[currentSectionKey];
  const userId = getValueFromLocalStorage(localStorageKey.USERDATA, "_id");

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }
    if (Object.entries(formData).length === 0) {
      fetchQuestionAnswers();
    }
  }, [currentSectionIndex]);

  const fetchQuestionAnswers = async () => {
    try {
      const response = await authorizedGet(
        `${configuration.apis.getAnswers}?userId=${userId}`,
      );

      if (response?.data?.status) {
        const { answers } = response.data.data;

        const mappedFormData = {};

        answers.forEach((section) => {
          const sectionKey = section.sectionId;
          mappedFormData[sectionKey] = {};

          section.questions.forEach((q) => {
            mappedFormData[sectionKey][q.id] = q.answer;
          });
        });

        setFormData(mappedFormData);
        setOriginalData(mappedFormData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleInputChange = (sectionKey, qId, value) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [qId]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSectionIndex < sectionKeys.length - 1) {
      setCurrentSectionIndex((prev) => prev + 1);
      setCurrentQuestionPage(0);
    }
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex((prev) => prev - 1);
      setCurrentQuestionPage(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const percentage = calculateProgressPercentage();
      const answers = Object.entries(formData).map(
        ([sectionKey, questionsMap]) => {
          return {
            sectionId: sectionKey,
            title: currentSection.title,
            questions: Object.entries(questionsMap).map(([id, answer]) => ({
              id,
              answer,
            })),
            sectionCompletionPercentage: getSectionProgress(sectionKey),
          };
        },
      );

      const data = {
        userId: userId,
        answers: answers,
        percentage: percentage,
      };

      console.log(data);

      const response = await authorizedPost(
        configuration.apis.saveQuestions,
        data,
        config,
      );

      if (response?.data?.status) {
        successNotify(response?.data?.message);
        setIsNext(true);
      } else {
        errorNotify(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleClick = (index) => {
    setCurrentSectionIndex(index);
    setCurrentQuestionPage(0);
  };

  //section completion percentage
  const getSectionProgress = (sectionKey) => {
    const answeredCount = Object.keys(formData[sectionKey] || {}).length;
    const totalCount = questions[sectionKey]?.questions.length || 0;

    const percentage =
      totalCount === 0 ? 0 : Math.round((answeredCount / totalCount) * 100);

    return percentage;
  };

  //overall completion percentage
  const calculateProgressPercentage = () => {
    let totalQuestions = 0;
    let completedQuestions = 0;

    Object.keys(questions).forEach((sectionKey) => {
      const sectionQuestions = questions[sectionKey]?.questions || [];

      totalQuestions += sectionQuestions.length;

      const answeredQuestions = formData[sectionKey] || {};
      const answeredCount = Object.keys(answeredQuestions).length;

      completedQuestions += answeredCount;
    });

    if (totalQuestions === 0 || completedQuestions === 0) return 0;

    console.log("totalQuestions " + totalQuestions);
    console.log("completedQuestions " + completedQuestions);
    return Math.round((completedQuestions / totalQuestions) * 100);
  };
  const isPageModified = () => {
    const currentPageQuestions = currentSection.questions?.slice(start, end);
    const savedAnswers = originalData[currentSectionKey] || {};
    const currentAnswers = formData[currentSectionKey] || {};

    const previousAnswers = {};
    currentPageQuestions.forEach((q) => {
      const savedAnswer = savedAnswers[q.id];
      if (savedAnswer !== undefined) {
        previousAnswers[q.id] = savedAnswer;
      }
    });

    const isModified = currentPageQuestions.some((q) => {
      return currentAnswers[q.id] !== previousAnswers[q.id];
    });

    return isModified;
  };

  //Questions pagniation logic
  const [currentQuestionPage, setCurrentQuestionPage] = useState(0);

  const QUESTIONS_PER_PAGE = 10;
  const start = currentQuestionPage * QUESTIONS_PER_PAGE;
  const end = (currentQuestionPage + 1) * QUESTIONS_PER_PAGE;
  const currentQuestions = currentSection?.questions?.slice(start, end);

  const totalQuestionPages = Math.ceil(
    currentSection.questions.length / QUESTIONS_PER_PAGE,
  );

  const handlePagnination = (direction) => {
    if (direction === "forward") {
      setCurrentQuestionPage((prev) =>
        Math.min(prev + 1, totalQuestionPages - 1),
      );
      if (currentQuestionPage === totalQuestionPages - 1) {
        handleNext();
      }

      if (isPageModified()) {
        handleSubmit();
      }
    } else if (direction === "backward") {
      setCurrentQuestionPage((prev) => Math.max(prev - 1, 0));
      if (currentQuestionPage === 0) {
        handleBack();
      }
    }
  };
  return (
    <div
      ref={containerRef}
      className="font-jost max-w-8xl mx-auto space-y-10 p-6 pt-24"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-textYellow sm:text-3xl">
          Section {currentSectionIndex + 1} of {sectionKeys.length}
        </h2>
        <p className="mt-2 text-base font-medium text-gray-600 sm:text-lg">
          {currentSection.title}
        </p>
        <div className="mt-6 overflow-x-auto">
          <div className="flex min-w-max justify-center px-4">
            <div className="flex items-center space-x-4">
              {sectionKeys.map((section, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`relative h-8 w-8 flex-shrink-0 rounded-full border-2 transition-all duration-300 xxs:h-12 xxs:w-12 md:h-12 md:w-12
                        ${
                          index === currentSectionIndex
                            ? " border-textYellow bg-textYellow text-white hover:border-[3px] hover:border-green-500"
                            : index < currentSectionIndex
                              ? "border-green-500 bg-green-500 text-white hover:border-[3px] hover:border-textYellow"
                              : "border-gray-300 bg-white text-gray-500 hover:border-[3px] hover:border-textYellow"
                        } flex cursor-pointer items-center justify-center text-sm font-semibold  sm:text-base`}
                    // onClick={() => handleClick(index)}
                  >
                    {index + 1}
                  </div>
                  {index < sectionKeys.length - 1 && (
                    <div
                      className={`h-1 w-2 transition-all duration-300 sm:w-10 md:w-12 ${
                        index < currentSectionIndex
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {currentQuestions?.map((q) => (
          <div
            key={q.id}
            className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
          >
            <label className="mb-2 block font-semibold text-gray-800">
              {q.id}. {q.question}
            </label>
            {q.options ? (
              <select
                className="w-full rounded-lg border border-textYellow bg-white p-3 focus:outline-none "
                value={formData[currentSectionKey]?.[q.id] || ""}
                onChange={(e) =>
                  handleInputChange(currentSectionKey, q.id, e.target.value)
                }
              >
                <option value="">Select an option</option>
                {q.options.map((opt, index) => (
                  <option key={index} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <textarea
                className="w-full rounded-lg border border-textYellow p-3 focus:outline-none "
                placeholder="Type your answer here..."
                value={formData[currentSectionKey]?.[q.id] || ""}
                onChange={(e) =>
                  handleInputChange(currentSectionKey, q.id, e.target.value)
                }
              />
            )}
          </div>
        ))}
        <div className="flex justify-between pt-6">
          <button
            onClick={() => handlePagnination("backward")}
            // disabled={currentQuestionPage === 0}
            disabled={currentSectionIndex === 0 && currentQuestionPage === 0}
            className={`  flex items-center gap-2 rounded-full px-2 py-2 text-sm font-medium transition-all duration-200 xxs:px-5 ${
              currentSectionIndex === 0 && currentQuestionPage === 0
                ? "cursor-not-allowed bg-gray-200 text-gray-400"
                : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
            }`}
          >
            <FaArrowLeft /> Previous
          </button>

          <div className="flex items-center space-x-2">
            {[...Array(totalQuestionPages)].map((_, i) => (
              <div
                key={i}
                className={`h-3 w-3 rounded-full ${
                  i === currentQuestionPage ? "bg-textYellow" : "bg-gray-300"
                }`}
              ></div>
            ))}
          </div>
          {currentSectionIndex === sectionKeys.length - 1 &&
          currentQuestionPage === totalQuestionPages - 1 ? (
            <button
              className="flex items-center gap-2 rounded-full bg-green-600 px-2 py-2 font-medium text-white shadow transition hover:bg-green-700 xxs:px-5"
              onClick={handleSubmit}
            >
              Submit <FaPaperPlane />
            </button>
          ) : (
            <button
              onClick={() => handlePagnination("forward")}
              // disabled={currentQuestionPage === totalQuestionPages - 1}
              className="flex items-center gap-2 rounded-full bg-textYellow px-2 py-2 font-medium text-white shadow transition hover:bg-yellow-700 disabled:bg-textYellow/20 xxs:px-5"
            >
              Next <FaArrowRight />
            </button>
          )}
        </div>

        <div className="flex w-full items-center justify-center gap-1 text-sm font-medium text-gray-700">
          <p>Section {currentSectionIndex + 1}</p>
          <p>/</p>
          <p>
            Page {currentQuestionPage + 1} of {totalQuestionPages}
          </p>
        </div>
      </div>

      {/* <div className="flex items-center justify-between pt-8">
        <button
          className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-all duration-200 ${
            currentSectionIndex === 0
              ? "cursor-not-allowed bg-gray-200 text-gray-400"
              : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
          }`}
          onClick={handleBack}
          disabled={currentSectionIndex === 0}
        >
          <FaArrowLeft /> Back
        </button>

      
      </div> */}
    </div>
  );
};

export default QuestionnaireForm;
